import { useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { ClockIcon, UsersIcon, MoonIcon, PencilIcon } from '@heroicons/react/outline'
import { updateWorkShift } from '../../../services/workforceServices'
import { invalidateWorkforce } from '../../../utils/workforceUtils'

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

const Toggle = ({ value, onChange, disabled }) => (
    <button
        type="button"
        role="switch"
        aria-checked={value}
        disabled={disabled}
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 ${value ? 'bg-emerald-500' : 'bg-gray-300'} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
        <span
            aria-hidden="true"
            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${value ? 'translate-x-4' : 'translate-x-0'}`}
        />
    </button>
)

const formatTime = (s) => (s || '').slice(0, 5)

export const ShiftCard = ({ shift, warehouse_id, operatorCount = 0, onEdit }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const toggleActive = useMutation(
        (next) => updateWorkShift(warehouse_id, shift.id, { active: next }),
        {
            onSuccess: () => invalidateWorkforce(queryClient, warehouse_id, ['shifts', 'attendance', 'costs', 'costs-range']),
        }
    )

    const scheduleDays = shift.schedule_days || []
    const overnight = shift.end_time && shift.start_time && formatTime(shift.end_time) < formatTime(shift.start_time)
    const isActive = !!shift.active

    return (
        <div
            className={`group relative flex flex-col rounded-xl border p-5 transition-all duration-150 ${
                isActive
                    ? 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                    : 'border-gray-200 bg-gray-50'
            }`}
        >
            <button
                type="button"
                onClick={() => onEdit(shift)}
                aria-label={t('workforce.shifts.edit_shift')}
                title={t('workforce.shifts.edit_shift')}
                className="absolute top-3 right-3 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-1"
            >
                <PencilIcon className="h-4 w-4" />
            </button>

            <div className="pr-10">
                <h3 className={`font-semibold truncate ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>{shift.name}</h3>
                {overnight && (
                    <span className="inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded-md text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                        <MoonIcon className="h-3 w-3" />
                        {t('workforce.shifts.overnight_badge')}
                    </span>
                )}
            </div>

            <div className="mt-4 flex gap-1">
                {DAY_KEYS.map((key, i) => {
                    const on = scheduleDays.includes(i)
                    const full = t(`workforce.days.${key}`)
                    return (
                        <span
                            key={key}
                            title={full}
                            aria-label={full}
                            className={`flex h-7 w-7 items-center justify-center rounded-md text-[11px] font-semibold ${
                                on
                                    ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                    : 'bg-white text-gray-300 border border-gray-100'
                            }`}
                        >
                            {full.charAt(0)}
                        </span>
                    )
                })}
            </div>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
                <ClockIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div className={`flex items-baseline gap-1.5 ${isActive ? 'text-gray-900' : 'text-gray-400'}`}>
                    <span className="text-2xl font-semibold tracking-tight tabular-nums">{formatTime(shift.start_time)}</span>
                    <span className="text-gray-400 text-lg leading-none">→</span>
                    <span className="text-2xl font-semibold tracking-tight tabular-nums">{formatTime(shift.end_time)}</span>
                    {overnight && <span className="text-xs font-medium text-amber-700 ml-0.5">+1</span>}
                </div>
                {shift.shift_period && (
                    <span
                        className={`ml-1 inline-flex items-center px-1.5 py-0.5 rounded-md text-[11px] font-medium border ${
                            shift.shift_period === 'night'
                                ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                : 'bg-yellow-50 text-yellow-800 border-yellow-200'
                        }`}
                    >
                        {t(`workforce.shifts.period_${shift.shift_period}`)}
                    </span>
                )}
            </div>

            {scheduleDays.length === 0 && (
                <div className="mt-3 text-xs italic text-gray-400">{t('workforce.shifts.no_days')}</div>
            )}

            <div className="mt-5 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                    <UsersIcon className="h-4 w-4 text-gray-400" />
                    <span>{t('workforce.shifts.operators_assigned', { count: operatorCount })}</span>
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-gray-600 select-none">
                    <span>{t('workforce.shifts.is_active')}</span>
                    <Toggle value={isActive} onChange={(v) => toggleActive.mutate(v)} disabled={toggleActive.isLoading} />
                </label>
            </div>
        </div>
    )
}
